import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Drawer,
    Fade,
    Grid,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material';
import {
    useGetFaqsQuery,
    useDeleteFaqGroupMutation,
} from '@starjack/entities/faq';
import { CardListRow } from '@starjack/shared-components';
import { Fragment, useState } from 'react';
import { BoldLabel } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import { createEmptyFaqGroup, FaqGroup } from '../model/faq.types';
import { GroupDrawerContent } from './groupDrawerContent';
import { useNotification } from '@starjack/features/notifications';
import { LoadingContainer } from '@starjack/shared-components';

export const FaqGroupConfiguration = () => {
    const { data } = useGetFaqsQuery({
        filtered: true,
    });
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        group: FaqGroup;
    }>({ isOpen: false, group: null });
    const [deleteFaqGroup] = useDeleteFaqGroupMutation();
    const { addNotification } = useNotification();
    const [deleteGroupId, setDeleteGroupId] = useState<string>('');

    const onDelete = (faqGroup: FaqGroup) => {
        setDeleteGroupId(faqGroup.id);
        deleteFaqGroup({ faqGroupId: faqGroup.id })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Themenbereich erfolgreich gelöscht',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Löschen. Bitte später noch einmal versuchen',
                });
            })
            .finally(() => {
                setDeleteGroupId(null);
            });
    };

    const onCloseDrawer = () => {
        setDrawerConfig({
            isOpen: false,
            group: null,
        });
    };

    return (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth='lg'
        >
            <Card>
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h6'>
                        Angelegte Themenbereiche
                    </Typography>

                    <Button
                        onClick={() => {
                            setDrawerConfig({
                                isOpen: true,
                                group: null,
                            });
                        }}
                        sx={{
                            ml: 'auto',
                        }}
                        variant='contained'
                    >
                        Themenbereich erstellen
                    </Button>
                </CardContent>
                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                {data?.map((group, index) => (
                    <Fragment key={index}>
                        <CardListRow
                            sx={{
                                position: 'relative',
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    flexGrow: 1,
                                }}
                            >
                                <Grid spacing={6} container>
                                    <Grid md={6} item>
                                        <BoldLabel>Deutsch:</BoldLabel>{' '}
                                        {group.name.de}
                                    </Grid>
                                    <Grid md={6} item>
                                        <BoldLabel>Priorität:</BoldLabel>{' '}
                                        {group.priority}
                                    </Grid>
                                    <Grid md={6} item>
                                        <BoldLabel>Englisch:</BoldLabel>{' '}
                                        {group.name.en || '- '}
                                    </Grid>
                                </Grid>
                            </Box>

                            <Stack
                                sx={{
                                    ml: 'auto',
                                    alignItems: 'center',
                                }}
                                direction='row'
                                spacing={2}
                            >
                                <IconButton
                                    onClick={() => {
                                        setDrawerConfig({
                                            isOpen: true,
                                            group,
                                        });
                                    }}
                                >
                                    <SvgIcon>
                                        <EditIcon />
                                    </SvgIcon>
                                </IconButton>

                                <IconButton onClick={() => onDelete(group)}>
                                    <SvgIcon>
                                        <DeleteIcon />
                                    </SvgIcon>
                                </IconButton>
                            </Stack>
                            <Fade in={deleteGroupId === group.id}>
                                <div>
                                    <LoadingContainer cover />
                                </div>
                            </Fade>
                        </CardListRow>

                        {index < data.length - 1 && (
                            <Divider
                                sx={{
                                    width: '100%',
                                }}
                            />
                        )}
                    </Fragment>
                ))}
            </Card>

            <Drawer
                anchor='right'
                open={drawerConfig.isOpen}
                onClose={onCloseDrawer}
            >
                <GroupDrawerContent
                    group={drawerConfig?.group || createEmptyFaqGroup()}
                    onClose={onCloseDrawer}
                />
            </Drawer>
        </Container>
    );
};
